"use client";

import type { HTMLAttributes, ReactNode } from "react";
import { CircleChevronDown, CircleChevronUp } from "lucide-react";

type QuestionProps = HTMLAttributes<HTMLSpanElement> & {
  [key: `data-${string}`]: string | boolean | undefined;
};

interface FaqAccordionItemProps {
  id: string;
  question: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  answerClassName?: string;
  questionProps?: QuestionProps;
}

export default function FaqAccordionItem({
  id,
  question,
  children,
  isOpen,
  onToggle,
  answerClassName = "",
  questionProps,
}: FaqAccordionItemProps) {
  const answerId = `${id}-answer`;
  const questionId = `${id}-question`;

  return (
    <div className="space-y-0">
      <button
        id={id}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
        className="w-full rounded-[27px] border-2 border-[#615D5D] px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:border-[#767EF6]/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#767EF6] focus:ring-inset"
      >
        <span
          {...questionProps}
          className={`text-base sm:text-lg font-semibold text-white pr-3 sm:pr-4 flex-1 text-left ${questionProps?.className ?? ""}`}
          id={questionProps?.id ?? questionId}
        >
          {question}
        </span>
        <span className="flex-shrink-0" aria-hidden="true">
          {isOpen ? (
            <CircleChevronUp className="h-5 w-5 sm:h-6 sm:w-6 text-[#000000] bg-[#767EF6] rounded-full transition-transform duration-300" />
          ) : (
            <CircleChevronDown className="h-5 w-5 sm:h-6 sm:w-6 text-[#000000] bg-[#767EF6] rounded-full transition-transform duration-300" />
          )}
        </span>
      </button>

      <div
        id={answerId}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
        role="region"
        aria-labelledby={id}
        aria-hidden={!isOpen}
      >
        <div className="overflow-hidden">
          <div className={`pt-3 pb-2 ${answerClassName || "ml-6"}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
